import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";

import { PageHero } from "@/components/layout/PageHero";
import { pages } from "@/content/pages";
import type { Locale } from "@/i18n/locales";

type AboutViewProps = {
  locale: Locale;
};

export function AboutView({ locale }: AboutViewProps) {
  const copy = pages[locale].about;

  return (
    <main className="bg-background">
      <PageHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        intro={copy.intro}
        className="pb-20"
      />

      <section className="bg-secondary/40 py-16">
        <div className="mx-auto max-w-4xl px-5">
          <span className="text-xs font-bold uppercase tracking-widest text-primary">
            {copy.storyEyebrow}
          </span>
          <h2 className="mt-3 font-heading text-3xl font-bold text-[#1a3a5c] md:text-4xl">
            {copy.storyTitle}
          </h2>
          <div className="mt-4 h-1 w-12 bg-accent" />
          <div className="mt-8 space-y-5 text-base leading-relaxed text-muted-foreground">
            {copy.story.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-4xl px-5">
          <div className="mb-14 text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              {copy.teamEyebrow}
            </span>
            <h2 className="mt-3 font-heading text-3xl font-bold text-[#1a3a5c] md:text-4xl">
              {copy.teamTitle}
            </h2>
            <div className="mx-auto mt-4 h-1 w-12 bg-accent" />
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {copy.team.map((member, index) => (
              <motion.article
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="overflow-hidden rounded-2xl border border-border bg-card transition-all duration-300 hover:border-primary/20 hover:shadow-lg"
              >
                <img
                  src={member.img}
                  alt={member.name}
                  width={800}
                  height={800}
                  loading="lazy"
                  className="aspect-square w-full object-cover object-top"
                />
                <div className="p-7">
                  <div className="mb-1 text-xs font-bold uppercase tracking-widest text-primary">
                    {member.role}
                  </div>
                  <h3 className="font-heading text-xl font-bold text-[#1a3a5c]">
                    {member.name}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    {member.bio}
                  </p>
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
                  >
                    <Linkedin className="h-4 w-4" />
                    {copy.linkedinLabel}
                  </a>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
